// =============================================================================
// HUB OPÉRATION - Planification Chantier
// =============================================================================
// Gestion des interventions avec intégration Google Calendar
// =============================================================================

import { useState, useEffect, useCallback } from "react";
import {
  Calendar,
  Clock,
  MapPin,
  User,
  Users,
  Phone,
  Mail,
  Plus,
  Edit,
  Trash2,
  RefreshCw,
  Bell,
  CheckCircle2,
  AlertCircle,
  Send,
  ExternalLink
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import { enteteTenant } from "../../utils/enteteTenant";

const API = process.env.REACT_APP_BACKEND_URL;

// Couleurs par statut
const STATUT_COLORS = {
  planifiee: { bg: "bg-blue-500/20", text: "text-blue-400", border: "border-blue-500/30" },
  confirmee: { bg: "bg-green-500/20", text: "text-green-400", border: "border-green-500/30" },
  en_cours: { bg: "bg-orange-500/20", text: "text-orange-400", border: "border-orange-500/30" },
  terminee: { bg: "bg-emerald-500/20", text: "text-emerald-400", border: "border-emerald-500/30" },
  annulee: { bg: "bg-red-500/20", text: "text-red-400", border: "border-red-500/30" }
};

const STATUT_LABELS = {
  planifiee: "Planifiée",
  confirmee: "Confirmée",
  en_cours: "En cours",
  terminee: "Terminée",
  annulee: "Annulée"
};

const TYPE_INTERVENTION_LABELS = {
  installation: "Installation",
  sav: "SAV",
  visite_technique: "Visite technique",
  maintenance: "Maintenance"
};

export default function PlanningChantier({ dossierId, tenantId, dossier }) {
  const [loading, setLoading] = useState(false);
  const [interventions, setInterventions] = useState([]);
  const [installateurs, setInstallateurs] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editingIntervention, setEditingIntervention] = useState(null);
  const [form, setForm] = useState({
    titre: "",
    description: "",
    date_debut: "",
    date_fin: "",
    heure_debut: "08:00",
    heure_fin: "17:00",
    installateur_id: "",
    installateur_nom: "",
    type_intervention: "installation",
    notifier_client: true,
    notifier_equipe: true
  });

  // Charger les interventions
  const loadInterventions = useCallback(async () => {
    if (!dossierId) return;
    
    setLoading(true);
    try {
      const headers = { ...enteteTenant(tenantId) };
      
      // Charger interventions
      const res = await fetch(`${API}/api/operation/planning/dossier/${dossierId}`, { headers });
      if (res.ok) {
        setInterventions(await res.json());
      }
      
      // Charger installateurs
      const instRes = await fetch(`${API}/api/operation/planning/installateurs`, { headers });
      if (instRes.ok) {
        setInstallateurs(await instRes.json());
      }
    } catch (error) {
      console.error("Erreur chargement planning:", error);
    } finally {
      setLoading(false);
    }
  }, [dossierId, tenantId]);

  useEffect(() => {
    loadInterventions();
  }, [loadInterventions]);

  // Ouvrir le dialog pour nouvelle intervention
  const openNewIntervention = () => {
    setEditingIntervention(null);
    setForm({
      titre: `Installation - ${dossier?.client?.nom || "Client"}`,
      description: "",
      date_debut: "",
      date_fin: "",
      heure_debut: "08:00",
      heure_fin: "17:00",
      installateur_id: "",
      installateur_nom: "",
      type_intervention: "installation",
      notifier_client: true,
      notifier_equipe: true
    });
    setShowDialog(true);
  };

  // Ouvrir le dialog pour édition
  const openEditIntervention = (intervention) => {
    setEditingIntervention(intervention);
    setForm({
      titre: intervention.titre || "",
      description: intervention.description || "",
      date_debut: intervention.date_debut?.split("T")[0] || "",
      date_fin: intervention.date_fin?.split("T")[0] || "",
      heure_debut: intervention.heure_debut || "08:00",
      heure_fin: intervention.heure_fin || "17:00",
      installateur_id: intervention.installateur_id || "",
      installateur_nom: intervention.installateur_nom || "",
      type_intervention: intervention.type_intervention || "installation",
      notifier_client: intervention.notifier_client !== false,
      notifier_equipe: intervention.notifier_equipe !== false
    });
    setShowDialog(true);
  };

  // Sauvegarder l'intervention
  const saveIntervention = async () => {
    if (!form.date_debut || !form.titre) {
      toast.error("Date et titre requis");
      return;
    }
    
    try {
      const headers = { 
        ...enteteTenant(tenantId),
        "Content-Type": "application/json"
      };
      
      const payload = {
        ...form,
        dossier_id: dossierId,
        date_fin: form.date_fin || form.date_debut,
        // Item 6 Phase C — sentinel "" / "none" → null
        installateur_id: form.installateur_id === "none" || !form.installateur_id ? null : form.installateur_id,
        installateur_nom: form.installateur_id === "none" || !form.installateur_id ? null : form.installateur_nom,
      };
      
      let res;
      if (editingIntervention) {
        res = await fetch(`${API}/api/operation/planning/${editingIntervention.id}`, {
          method: "PUT",
          headers,
          body: JSON.stringify(payload)
        });
      } else {
        res = await fetch(`${API}/api/operation/planning/intervention`, {
          method: "POST",
          headers,
          body: JSON.stringify(payload)
        });
      }
      
      if (res.ok) {
        toast.success(editingIntervention ? "Intervention mise à jour" : "Intervention planifiée");
        setShowDialog(false);
        loadInterventions();
      } else {
        const data = await res.json();
        toast.error(data.detail || "Erreur lors de la sauvegarde");
      }
    } catch (error) {
      console.error("Erreur save:", error);
      toast.error("Erreur lors de la sauvegarde");
    }
  };

  // Supprimer une intervention
  const deleteIntervention = async (interventionId) => {
    if (!confirm("Supprimer cette intervention ?")) return;
    
    try {
      const headers = { ...enteteTenant(tenantId) };
      const res = await fetch(`${API}/api/operation/planning/${interventionId}`, {
        method: "DELETE",
        headers
      });
      
      if (res.ok) {
        toast.success("Intervention supprimée");
        loadInterventions();
      } else {
        toast.error("Erreur lors de la suppression");
      }
    } catch (error) {
      console.error("Erreur delete:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  // Envoyer un rappel
  const envoyerRappel = async (interventionId, type = "both") => {
    try {
      const headers = { 
        ...enteteTenant(tenantId),
        "Content-Type": "application/json"
      };
      
      const res = await fetch(`${API}/api/operation/planning/${interventionId}/rappel`, {
        method: "POST",
        headers,
        body: JSON.stringify({ type })
      });
      
      if (res.ok) {
        toast.success("Rappel envoyé");
        loadInterventions();
      } else {
        toast.error("Erreur lors de l'envoi");
      }
    } catch (error) {
      console.error("Erreur rappel:", error);
      toast.error("Erreur lors de l'envoi du rappel");
    }
  };

  // Mettre à jour le statut
  const updateStatut = async (interventionId, nouveauStatut) => {
    try {
      const headers = { 
        ...enteteTenant(tenantId),
        "Content-Type": "application/json"
      };
      
      const res = await fetch(`${API}/api/operation/planning/${interventionId}`, {
        method: "PUT",
        headers,
        body: JSON.stringify({ statut: nouveauStatut })
      });
      
      if (res.ok) {
        toast.success("Statut mis à jour");
        loadInterventions();
      } else {
        toast.error("Erreur lors de la mise à jour");
      }
    } catch (error) {
      console.error("Erreur update:", error);
      toast.error("Erreur lors de la mise à jour");
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h4 className="text-white font-medium flex items-center gap-2">
          <Calendar className="w-5 h-5 text-orange-400" />
          Planning Interventions
        </h4>
        <div className="flex gap-2">
          <Button
            size="sm"
            onClick={openNewIntervention}
            className="bg-orange-600 hover:bg-orange-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Planifier
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={loadInterventions}
            disabled={loading}
            className="border-slate-600"
            aria-label="Rafraîchir le planning"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Liste des interventions */}
      {loading ? (
        <div className="flex justify-center py-8">
          <RefreshCw className="w-6 h-6 text-slate-400 animate-spin" />
        </div>
      ) : interventions.length === 0 ? (
        <div className="text-center py-8 bg-slate-700/30 rounded-lg">
          <Calendar className="w-12 h-12 mx-auto text-zinc-400 mb-3" />
          <p className="text-slate-400">Aucune intervention planifiée</p>
          <p className="text-zinc-400 text-sm mt-1">
            Cliquez sur "Planifier" pour créer une intervention
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {interventions.map((inter) => {
            const colors = STATUT_COLORS[inter.statut] || STATUT_COLORS.planifiee;
            
            return (
              <div 
                key={inter.id}
                className={`bg-slate-700/30 rounded-lg p-4 border ${colors.border}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    {/* Titre et statut */}
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-white font-medium">
                        {inter.titre}
                      </span>
                      <Badge className={`${colors.bg} ${colors.text} border ${colors.border}`}>
                        {STATUT_LABELS[inter.statut] || inter.statut}
                      </Badge>
                      <Badge className="bg-slate-600 text-slate-300">
                        {TYPE_INTERVENTION_LABELS[inter.type_intervention] || inter.type_intervention}
                      </Badge>
                    </div>
                    
                    {/* Détails */}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                      <div className="flex items-center gap-2 text-slate-400">
                        <Calendar className="w-4 h-4" />
                        <span>
                          {new Date(inter.date_debut).toLocaleDateString("fr-FR")}
                          {inter.date_fin && inter.date_fin !== inter.date_debut && (
                            <> - {new Date(inter.date_fin).toLocaleDateString("fr-FR")}</>
                          )}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-400">
                        <Clock className="w-4 h-4" />
                        <span>{inter.heure_debut} - {inter.heure_fin}</span>
                      </div>
                      {inter.installateur_nom && (
                        <div className="flex items-center gap-2 text-slate-400">
                          <User className="w-4 h-4" />
                          <span>{inter.installateur_nom}</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Google Calendar */}
                    {inter.google_event_id && (
                      <div className="mt-2 flex items-center gap-2">
                        <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 text-xs">
                          <ExternalLink className="w-3 h-3 mr-1" />
                          Google Calendar
                        </Badge>
                      </div>
                    )}
                    
                    {/* Rappels envoyés */}
                    {inter.rappels_envoyes?.length > 0 && (
                      <div className="mt-2">
                        <span className="text-xs text-zinc-400">
                          {inter.rappels_envoyes.length} rappel(s) envoyé(s)
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-1 ml-4">
                    {/* Changement de statut */}
                    <Select
                      value={inter.statut}
                      onValueChange={(value) => updateStatut(inter.id, value)}
                    >
                      <SelectTrigger className="w-28 h-8 bg-slate-800 border-slate-600 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(STATUT_LABELS).map(([code, label]) => (
                          <SelectItem key={code} value={code}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => envoyerRappel(inter.id)}
                      className="text-amber-400 hover:text-amber-300 h-8 w-8 p-0"
                      title="Envoyer rappel"
                    >
                      <Bell className="w-4 h-4" />
                    </Button>
                    
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => openEditIntervention(inter)}
                      className="text-violet-400 hover:text-violet-300 h-8 w-8 p-0"
                      title="Modifier"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => deleteIntervention(inter.id)}
                      className="text-red-400 hover:text-red-300 h-8 w-8 p-0"
                      title="Supprimer"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Dialog création/édition */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              {editingIntervention ? "Modifier l'intervention" : "Planifier une intervention"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-slate-300">Titre</Label>
              <Input
                value={form.titre}
                onChange={(e) => setForm(prev => ({ ...prev, titre: e.target.value }))}
                placeholder="Ex: Installation PAC"
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
            </div>
            
            <div>
              <Label className="text-slate-300">Type d'intervention</Label>
              <Select
                value={form.type_intervention}
                onValueChange={(value) => setForm(prev => ({ ...prev, type_intervention: value }))}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(TYPE_INTERVENTION_LABELS).map(([code, label]) => (
                    <SelectItem key={code} value={code}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Date début</Label>
                <Input
                  type="date"
                  value={form.date_debut}
                  onChange={(e) => setForm(prev => ({ ...prev, date_debut: e.target.value }))}
                  className="bg-slate-800 border-slate-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-slate-300">Date fin (optionnel)</Label>
                <Input
                  type="date"
                  value={form.date_fin}
                  onChange={(e) => setForm(prev => ({ ...prev, date_fin: e.target.value }))}
                  className="bg-slate-800 border-slate-700 text-white mt-1"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Heure début</Label>
                <Input
                  type="time"
                  value={form.heure_debut}
                  onChange={(e) => setForm(prev => ({ ...prev, heure_debut: e.target.value }))}
                  className="bg-slate-800 border-slate-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-slate-300">Heure fin</Label>
                <Input
                  type="time"
                  value={form.heure_fin}
                  onChange={(e) => setForm(prev => ({ ...prev, heure_fin: e.target.value }))}
                  className="bg-slate-800 border-slate-700 text-white mt-1"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-slate-300">Installateur</Label>
              <Select
                value={form.installateur_id}
                onValueChange={(value) => {
                  const inst = installateurs.find(i => i.id === value);
                  setForm(prev => ({ 
                    ...prev, 
                    installateur_id: value,
                    installateur_nom: inst?.nom || ""
                  }));
                }}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1">
                  <SelectValue placeholder="Sélectionner un installateur" />
                </SelectTrigger>
                <SelectContent>
                  {installateurs.map((inst) => (
                    <SelectItem key={inst.id} value={inst.id}>
                      {inst.nom} {inst.prenom}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-slate-300">Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Notes sur l'intervention..."
                className="bg-slate-800 border-slate-700 text-white mt-1"
                rows={3}
              />
            </div>
            
            <div className="flex items-center justify-between bg-slate-800 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <Bell className="w-4 h-4 text-amber-400" />
                <span className="text-slate-300 text-sm">Notifications</span>
              </div>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.notifier_client}
                    onChange={(e) => setForm(prev => ({ ...prev, notifier_client: e.target.checked }))}
                    className="rounded border-slate-600"
                  />
                  <span className="text-slate-400">Client</span>
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.notifier_equipe}
                    onChange={(e) => setForm(prev => ({ ...prev, notifier_equipe: e.target.checked }))}
                    className="rounded border-slate-600"
                  />
                  <span className="text-slate-400">Équipe</span>
                </label>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDialog(false)}
              className="border-slate-600"
            >
              Annuler
            </Button>
            <Button
              onClick={saveIntervention}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {editingIntervention ? "Mettre à jour" : "Planifier"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
